import { useState, useEffect } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { joinPersonName, initialsFor } from '@/utils/names';
import {
  ArrowLeft, Mail, Phone, Building2, Loader2, User, Plus, X,
  MessageSquare, PhoneCall, CalendarDays, StickyNote,
  Target, Receipt, Clock, Send, CheckCircle, AlertCircle,
  Sparkles, Copy,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate, formatINR } from '@/utils/formatters';

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title?: string;
  source?: string;
  status?: string;
  leadScore?: number;
  productInterest?: string;
  city?: string;
  state?: string;
  notes?: string;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
  account?: { id: string; name: string } | null;
  customFields?: Record<string, string>;
}

interface Activity {
  id: string;
  type: string;
  description: string;
  createdAt: string;
  createdBy?: { firstName: string; lastName: string } | null;
}

interface Deal {
  id: string;
  title: string;
  value: number;
  probability?: number;
  stage?: { id: string; name: string } | null;
  createdAt: string;
}

interface Invoice {
  id: string;
  number: string;
  status: string;
  total: number;
  amountDue: number;
  issueDate: string;
  dueDate: string;
}

type TabKey = 'overview' | 'activities' | 'deals' | 'invoices';

const ACTIVITY_TYPES = [
  { value: 'note', label: 'Note', icon: StickyNote },
  { value: 'call', label: 'Call', icon: PhoneCall },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'meeting', label: 'Meeting', icon: CalendarDays },
];

const ACTIVITY_ICONS: Record<string, typeof StickyNote> = {
  note: StickyNote,
  call: PhoneCall,
  email: Mail,
  meeting: CalendarDays,
};

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  // Activities
  const [activities, setActivities] = useState<Activity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  // Deals
  const [deals, setDeals] = useState<Deal[]>([]);
  const [dealsLoading, setDealsLoading] = useState(false);

  // Invoices
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  // Add Activity Modal
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityForm, setActivityForm] = useState({ type: 'note', description: '' });
  const [activitySaving, setActivitySaving] = useState(false);

  // AI Features
  const [scoringLead, setScoringLead] = useState(false);
  const [scoreBreakdown, setScoreBreakdown] = useState<Record<string, number> | null>(null);
  const [showEmailDraft, setShowEmailDraft] = useState(false);
  const [emailPurpose, setEmailPurpose] = useState('follow_up');
  const [draftingEmail, setDraftingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState<{ subject: string; body: string } | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // WhatsApp Send
  const [chatKartConnected, setChatKartConnected] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [waMessage, setWaMessage] = useState('');
  const [waSending, setWaSending] = useState(false);
  const [waStatus, setWaStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Check ChatKart connection status on mount
  useEffect(() => {
    api.get('/ecosystem/connections')
      .then(res => {
        const ck = res.data.data?.find((c: any) => c.product === 'chatkart');
        setChatKartConnected(ck?.connected === true);
      })
      .catch(() => {});
  }, []);

  const handleSendWhatsApp = async () => {
    if (!contact?.phone || !waMessage.trim()) return;
    try {
      setWaSending(true);
      setWaStatus(null);
      await api.post('/ecosystem/chatkart/send', {
        phone: contact.phone,
        text: waMessage.trim(),
      });
      setWaStatus({ type: 'success', text: 'WhatsApp message sent successfully' });
      setWaMessage('');
      setTimeout(() => {
        setShowWhatsAppModal(false);
        setWaStatus(null);
      }, 1500);
    } catch (err: any) {
      setWaStatus({ type: 'error', text: err.response?.data?.error || 'Failed to send WhatsApp message' });
    } finally {
      setWaSending(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/contacts/${id}`)
      .then(res => setContact(res.data.data))
      .catch(err => setError(err.response?.data?.error || 'Failed to load contact'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id || activeTab !== 'activities') return;
    setActivitiesLoading(true);
    api.get('/activities', { params: { contactId: id } })
      .then(res => setActivities(res.data.data?.activities ?? res.data.data ?? []))
      .catch(() => {})
      .finally(() => setActivitiesLoading(false));
  }, [id, activeTab]);

  useEffect(() => {
    if (!id || activeTab !== 'deals') return;
    setDealsLoading(true);
    api.get('/deals', { params: { contactId: id } })
      .then(res => setDeals(res.data.data?.deals ?? res.data.data ?? []))
      .catch(() => {})
      .finally(() => setDealsLoading(false));
  }, [id, activeTab]);

  useEffect(() => {
    if (!id || activeTab !== 'invoices') return;
    setInvoicesLoading(true);
    api.get('/invoices', { params: { contactId: id } })
      .then(res => setInvoices(res.data.data?.invoices ?? res.data.data ?? []))
      .catch(() => {})
      .finally(() => setInvoicesLoading(false));
  }, [id, activeTab]);

  const handleAddActivity = async () => {
    if (!id || !activityForm.description.trim()) return;
    try {
      setActivitySaving(true);
      await api.post('/activities', {
        type: activityForm.type,
        description: activityForm.description,
        contactId: id,
      });
      setShowActivityModal(false);
      setActivityForm({ type: 'note', description: '' });
      // Refresh activities
      const res = await api.get('/activities', { params: { contactId: id } });
      setActivities(res.data.data?.activities ?? res.data.data ?? []);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to add activity');
    } finally {
      setActivitySaving(false);
    }
  };

  const handleScoreLead = async () => {
    if (!id) return;
    try {
      setScoringLead(true);
      const res = await api.post('/ai/lead-score', { contactId: id });
      const data = res.data.data ?? res.data;
      setContact(prev => prev ? { ...prev, leadScore: data.score } : prev);
      setScoreBreakdown(data.breakdown ?? null);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to score lead. You may not have enough AI credits.');
    } finally {
      setScoringLead(false);
    }
  };

  const handleDraftEmail = async () => {
    if (!id) return;
    try {
      setDraftingEmail(true);
      const res = await api.post('/ai/email-draft', { contactId: id, purpose: emailPurpose });
      const data = res.data.data ?? res.data;
      setEmailDraft({ subject: data.subject, body: data.body });
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to draft email. You may not have enough AI credits.');
    } finally {
      setDraftingEmail(false);
    }
  };

  const handleGetSuggestions = async () => {
    if (!id) return;
    try {
      setLoadingSuggestions(true);
      const res = await api.get(`/ai/suggestions/contact/${id}`);
      const data = res.data.data ?? res.data;
      setSuggestions(data.suggestions ?? []);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to get suggestions. You may not have enough AI credits.');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-gray-500">Loading contact...</span>
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="text-sm text-red-500">{error || 'Contact not found'}</p>
        <button onClick={() => navigate('/contacts')} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">
          Back to Contacts
        </button>
      </div>
    );
  }

  const tabs: { key: TabKey; label: string; icon: typeof User }[] = [
    { key: 'overview', label: 'Overview', icon: User },
    { key: 'activities', label: 'Activities', icon: Clock },
    { key: 'deals', label: 'Deals', icon: Target },
    { key: 'invoices', label: 'Invoices', icon: Receipt },
  ];

  const statusBadgeColors: Record<string, string> = {
    lead: 'bg-yellow-50 text-yellow-700',
    prospect: 'bg-blue-50 text-blue-700',
    customer: 'bg-green-50 text-green-700',
    inactive: 'bg-gray-100 text-gray-600',
  };

  return (
    <div>
      {/* Back button */}
      <button
        onClick={() => navigate('/contacts')}
        className="mb-4 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft size={16} />
        Back to Contacts
      </button>

      {/* Contact Header */}
      <div className="mb-6 rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--saffron-50)] text-[var(--ink-0)] text-xl font-bold">
              {initialsFor(contact.firstName, contact.lastName)}
            </div>
            <div>
              <h1 className="text-xl font-bold text-[var(--text-primary)]">
                {joinPersonName(contact.firstName, contact.lastName)}
              </h1>
              {contact.title && (
                <p className="text-sm text-[var(--text-muted)]">{contact.title}</p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-[var(--text-secondary)]">
                {contact.email && (
                  <span className="flex items-center gap-1">
                    <Mail size={14} className="text-[var(--text-muted)]" />
                    {contact.email}
                  </span>
                )}
                {contact.phone && (
                  /* BUG-C03 fix (Wave-3 May 1 2026): phone was plain text; wrap in tel: link */
                  <a
                    href={`tel:${contact.phone.replace(/\s+/g, '')}`}
                    className="flex items-center gap-1 hover:underline text-inherit"
                  >
                    <Phone size={14} className="text-[var(--text-muted)]" />
                    {contact.phone}
                  </a>
                )}
                {contact.account && (
                  <Link
                    to={`/accounts/${contact.account.id}`}
                    className="flex items-center gap-1 text-[var(--ink-0)] hover:underline"
                  >
                    <Building2 size={14} />
                    {contact.account.name}
                  </Link>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Add Activity — Apr 23, 2026 (Bug 5): previously only reachable via
                the Activities tab, so users working from the Overview tab had
                no visible way to log a call / note / meeting. Surfacing it in
                the contact header keeps it one click away from any tab. */}
            <button
              onClick={() => { setActivityForm({ type: 'note', description: '' }); setShowActivityModal(true); }}
              className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 transition-colors"
              title="Log a call, meeting, email, or note"
            >
              <Plus size={14} />
              Add Activity
            </button>
            {/* AI Buttons */}
            <button
              onClick={handleScoreLead}
              disabled={scoringLead}
              className="flex items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
              title="Score Lead (2 credits)"
            >
              {scoringLead ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Score Lead
            </button>
            <button
              onClick={() => { setShowEmailDraft(!showEmailDraft); setEmailDraft(null); }}
              className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 transition-colors"
              title="AI Email Draft (3 credits)"
            >
              <Sparkles size={14} />
              AI Email
            </button>
            <button
              onClick={handleGetSuggestions}
              disabled={loadingSuggestions}
              className="flex items-center gap-1.5 rounded-lg border border-purple-500/50 bg-purple-500/10 px-3 py-1.5 text-sm font-medium text-purple-600 hover:bg-purple-500/20 disabled:opacity-50 transition-colors"
              title="Get AI Suggestions (1 credit)"
            >
              {loadingSuggestions ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Suggestions
            </button>
            {chatKartConnected && contact.phone && (
              <button
                onClick={() => { setShowWhatsAppModal(true); setWaStatus(null); setWaMessage(''); }}
                className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 transition-colors"
                title="Send WhatsApp Message"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Send WhatsApp
              </button>
            )}
            {contact.status && (
              <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${statusBadgeColors[contact.status] || 'bg-gray-100 text-gray-600'}`}>
                {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
              </span>
            )}
            {contact.leadScore != null && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[var(--saffron-50)] px-3 py-1 text-xs font-medium text-[var(--ink-0)]">
                Score: {contact.leadScore}
              </span>
            )}
          </div>
        </div>

        {/* Score Breakdown */}
        {scoreBreakdown && (
          <div className="mt-4 rounded-lg border border-purple-500/20 bg-purple-500/5 p-3">
            <p className="mb-2 text-xs font-semibold text-purple-600 uppercase tracking-wider flex items-center gap-1">
              <Sparkles size={12} /> Lead Score Breakdown
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(scoreBreakdown).map(([key, val]) => (
                <span key={key} className="inline-flex items-center gap-1 rounded-full border border-[var(--border-primary)] bg-[var(--bg-inset)] px-2.5 py-1 text-xs text-[var(--text-secondary)]">
                  <span className="font-medium text-[var(--text-primary)]">{key}:</span> {val}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* AI Suggestions */}
        {suggestions.length > 0 && (
          <div className="mt-4 rounded-lg border border-indigo-500/20 bg-indigo-500/5 p-3">
            <p className="mb-2 text-xs font-semibold text-indigo-600 uppercase tracking-wider flex items-center gap-1">
              <Sparkles size={12} /> AI Suggestions
            </p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s, i) => (
                <span key={i} className="inline-flex rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-1.5 text-xs text-[var(--text-secondary)]">
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* AI Email Draft Dropdown */}
        {showEmailDraft && (
          <div className="mt-4 rounded-lg border border-indigo-500/20 bg-[var(--bg-inset)] p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-1.5">
                <Sparkles size={14} className="text-indigo-500" /> AI Email Draft
                <span className="text-xs font-normal text-[var(--text-muted)]">(3 credits)</span>
              </p>
              <button onClick={() => setShowEmailDraft(false)} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                <X size={16} />
              </button>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Purpose</label>
                <select
                  value={emailPurpose}
                  onChange={(e) => setEmailPurpose(e.target.value)}
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="follow_up">Follow Up</option>
                  <option value="introduction">Introduction</option>
                  <option value="meeting_request">Meeting Request</option>
                  <option value="thank_you">Thank You</option>
                  <option value="payment_reminder">Payment Reminder</option>
                </select>
              </div>
              <button
                onClick={handleDraftEmail}
                disabled={draftingEmail}
                className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {draftingEmail ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                Draft
              </button>
            </div>
            {emailDraft && (
              <div className="mt-3 space-y-2">
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <label className="text-xs font-medium text-[var(--text-muted)]">Subject</label>
                    <button onClick={() => copyToClipboard(emailDraft.subject)} className="text-xs text-indigo-500 hover:text-indigo-600 flex items-center gap-1">
                      <Copy size={10} /> Copy
                    </button>
                  </div>
                  <div className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)]">
                    {emailDraft.subject}
                  </div>
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <label className="text-xs font-medium text-[var(--text-muted)]">Body</label>
                    <button onClick={() => copyToClipboard(emailDraft.body)} className="text-xs text-indigo-500 hover:text-indigo-600 flex items-center gap-1">
                      <Copy size={10} /> Copy
                    </button>
                  </div>
                  <textarea
                    readOnly
                    value={emailDraft.body}
                    rows={6}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] resize-y"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-4 flex gap-1 border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'border-[var(--ink-0)] text-[var(--ink-0)]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        {activeTab === 'overview' && (
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-900 uppercase tracking-wider">Contact Information</h3>
              <dl className="space-y-3">
                <InfoRow label="Full Name" value={`${joinPersonName(contact.firstName, contact.lastName)}`} />
                <InfoRow label="Email" value={contact.email} />
                {/* BUG-C03 fix (Wave-3 May 1 2026): Phone as tel: link instead of plain text */}
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-500">Phone</dt>
                  <dd className="font-medium text-gray-900">
                    {contact.phone
                      ? <a href={`tel:${contact.phone.replace(/\s+/g, '')}`} className="hover:underline">{contact.phone}</a>
                      : '-'}
                  </dd>
                </div>
                <InfoRow label="Title" value={contact.title || '-'} />
                <InfoRow label="Product Interest" value={contact.productInterest || '-'} />
                <InfoRow label="Account" value={contact.account?.name || '-'} />
                <InfoRow label="Source" value={contact.source || '-'} />
                <InfoRow label="Created" value={formatDate(contact.createdAt)} />
                <InfoRow label="Updated" value={formatDate(contact.updatedAt)} />
              </dl>

              {/* Location */}
              {(contact.city || contact.state) && (
                <>
                  <h3 className="mb-3 mt-6 text-sm font-semibold text-gray-900 uppercase tracking-wider">Location</h3>
                  <dl className="space-y-3">
                    <InfoRow label="City" value={contact.city || '-'} />
                    <InfoRow label="State" value={contact.state || '-'} />
                  </dl>
                </>
              )}

              {/* Notes */}
              {contact.notes && (
                <>
                  <h3 className="mb-3 mt-6 text-sm font-semibold text-gray-900 uppercase tracking-wider">Notes</h3>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{contact.notes}</p>
                </>
              )}
            </div>
            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-900 uppercase tracking-wider">Tags</h3>
              {contact.tags && contact.tags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {contact.tags.map(tag => (
                    <span key={tag} className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No tags</p>
              )}

              {contact.customFields && Object.keys(contact.customFields).length > 0 && (
                <>
                  <h3 className="mb-3 mt-6 text-sm font-semibold text-gray-900 uppercase tracking-wider">Custom Fields</h3>
                  <dl className="space-y-3">
                    {Object.entries(contact.customFields).map(([key, value]) => (
                      <InfoRow key={key} label={key} value={value} />
                    ))}
                  </dl>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'activities' && (
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Activity Timeline</h3>
              <button
                onClick={() => setShowActivityModal(true)}
                className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
              >
                <Plus size={14} />
                Add Activity
              </button>
            </div>

            {activitiesLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 size={20} className="animate-spin text-[var(--saffron-600)]" />
              </div>
            ) : activities.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <MessageSquare size={32} className="mb-2" />
                <p className="text-sm">No activities yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {activities.map(activity => {
                  const Icon = ACTIVITY_ICONS[activity.type] || StickyNote;
                  return (
                    <div key={activity.id} className="flex gap-3">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                        <Icon size={14} className="text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-gray-900 capitalize">{activity.type}</span>
                          {activity.createdBy && (
                            <span className="text-gray-500">
                              by {activity.createdBy.firstName} {activity.createdBy.lastName}
                            </span>
                          )}
                          <span className="text-gray-400">{formatDate(activity.createdAt)}</span>
                        </div>
                        <p className="mt-1 text-sm text-gray-600">{activity.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'deals' && (
          <div>
            <h3 className="mb-4 text-sm font-semibold text-gray-900 uppercase tracking-wider">Deals</h3>
            {dealsLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 size={20} className="animate-spin text-[var(--saffron-600)]" />
              </div>
            ) : deals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <Target size={32} className="mb-2" />
                <p className="text-sm">No deals associated with this contact</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-3">Title</th>
                      <th className="px-4 py-3">Value</th>
                      <th className="px-4 py-3">Stage</th>
                      <th className="px-4 py-3">Probability</th>
                      <th className="px-4 py-3">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {deals.map(deal => (
                      <tr key={deal.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{deal.title}</td>
                        <td className="px-4 py-3 text-gray-600">{formatINR(deal.value)}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                            {deal.stage?.name || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{deal.probability != null ? `${deal.probability}%` : '-'}</td>
                        <td className="px-4 py-3 text-gray-500">{formatDate(deal.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'invoices' && (
          <div>
            <h3 className="mb-4 text-sm font-semibold text-gray-900 uppercase tracking-wider">Invoices</h3>
            {invoicesLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 size={20} className="animate-spin text-[var(--saffron-600)]" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <Receipt size={32} className="mb-2" />
                <p className="text-sm">No invoices for this contact</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-3">Number</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Issue Date</th>
                      <th className="px-4 py-3">Due Date</th>
                      <th className="px-4 py-3 text-right">Total</th>
                      <th className="px-4 py-3 text-right">Amount Due</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {invoices.map(inv => (
                      <tr key={inv.id} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/invoices/${inv.id}`)}>
                        <td className="px-4 py-3 font-medium text-[var(--ink-0)]">{inv.number}</td>
                        <td className="px-4 py-3"><InvoiceStatusBadge status={inv.status} /></td>
                        <td className="px-4 py-3 text-gray-600">{formatDate(inv.issueDate)}</td>
                        <td className="px-4 py-3 text-gray-600">{formatDate(inv.dueDate)}</td>
                        <td className="px-4 py-3 text-right text-gray-900 font-medium">{formatINR(inv.total)}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{formatINR(inv.amountDue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Send WhatsApp Modal */}
      {showWhatsAppModal && (
        <Modal open onClose={() => setShowWhatsAppModal(false)} className="!max-w-md">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Send WhatsApp
              </h2>
              <RequestCloseButton className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="text"
                  value={contact.phone || ''}
                  disabled
                  className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-600"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Message *</label>
                <textarea
                  rows={4}
                  value={waMessage}
                  onChange={(e) => setWaMessage(e.target.value)}
                  placeholder="Type your WhatsApp message..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                />
              </div>
              {waStatus && (
                <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${
                  waStatus.type === 'success'
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                }`}>
                  {waStatus.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                  {waStatus.text}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <RequestCloseButton
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </RequestCloseButton>
              <button
                onClick={handleSendWhatsApp}
                disabled={waSending || !waMessage.trim()}
                className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                {waSending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Send Message
              </button>
            </div>
        </Modal>
      )}

      {/* Add Activity Modal */}
      {showActivityModal && (
        <Modal open onClose={() => setShowActivityModal(false)} className="!max-w-md">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Add Activity</h2>
              <RequestCloseButton className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Type</label>
                <div className="grid grid-cols-4 gap-2">
                  {ACTIVITY_TYPES.map(t => (
                    <button
                      key={t.value}
                      onClick={() => setActivityForm({ ...activityForm, type: t.value })}
                      className={`flex flex-col items-center gap-1 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                        activityForm.type === t.value
                          ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <t.icon size={16} />
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Description *</label>
                <textarea
                  rows={4}
                  value={activityForm.description}
                  onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                  placeholder="Enter activity details..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <RequestCloseButton
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </RequestCloseButton>
              <button
                onClick={handleAddActivity}
                disabled={activitySaving || !activityForm.description.trim()}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {activitySaving && <Loader2 size={14} className="animate-spin" />}
                Add Activity
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium text-gray-900">{value}</dd>
    </div>
  );
}

function InvoiceStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-600',
    sent: 'bg-blue-50 text-blue-700',
    partial: 'bg-yellow-50 text-yellow-700',
    paid: 'bg-green-50 text-green-700',
    overdue: 'bg-red-50 text-red-700',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}
